import { CurrenciesContext } from '@/currencies/currency.context';
import { useCountries } from '@/features/card-account/hooks/useCardAccounts.hook';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CountrysData
  extends Array<{
    name?: string;
    code?: string;
  }> {}

export interface CountryData {
  name?: string;
  code?: string;
}

interface UseCountrySelectorParams {
  value?: string;
  onValueChange: (value: string) => void;
  onCountryChange?: (value: CountryData | undefined) => void;
}

export const useCountrySelector = ({ value, onValueChange, onCountryChange }: UseCountrySelectorParams) => {
  const { t } = useTranslation('earn');
  const currencies = useContext(CurrenciesContext);

  const { countries } = useCountries({});
  const isLoading = countries?.isLoading;
  useEffect(() => {
    countries?.refetch();
  }, []);

  const countrysData = useMemo(() => {
    if (!countries?.data?.data?.countries) return [];
    let data: CountrysData = [];
    data = countries?.data?.data?.countries
      .sort((a, b) => Number(a?.name?.charCodeAt(0)) - Number(b?.name?.charCodeAt(0)))
      .map((c) => {
        return { name: t(`countries::${c?.code}`) || c?.name, code: c?.code };
      });
    return data;
  }, [countries]);

  const [hasFocus, setHasFocus] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCountryId, setSelectedCountryId] = useState(value || '');

  const selectedCountry: CountryData | undefined = useMemo(() => {
    return countrysData.find((country) => country?.code === selectedCountryId);
  }, [value, selectedCountryId, countrysData]);

  const displayCountryData = useMemo(() => {
    if (!searchText) return countrysData;
    return countrysData.filter((country) => {
      return country?.name?.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [searchText, countrysData]);

  useEffect(() => {
    if (value) setSelectedCountryId(value);
  }, [value]);

  useEffect(() => {
    if (!!selectedCountry && !!onCountryChange) {
      onCountryChange(selectedCountry);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (!hasFocus) return;
    const close = () => {
      setHasFocus(false);
    };
    document.addEventListener('click', close);
    return () => {
      document.removeEventListener('click', close);
    };
  }, [hasFocus]);

  const changeFocus = (value: boolean) => setHasFocus(value);
  const changeSearchText = (value: string) => setSearchText(value);
  const selectCountryId = (id: string) => {
    setSelectedCountryId(id);
    onValueChange(id);
  };
  const getDecimalsByAsset = (asset: string) => {
    return currencies.currenciesDict?.[asset]?.decimals || 0;
  };

  return {
    hasFocus,
    selectedCountry,
    selectedCountryId,
    displayCountryData,
    changeFocus,
    changeSearchText,
    selectCountryId,
    getDecimalsByAsset,
    isLoading
  };
};
